import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useTextCommands } from '../../hooks/useTextCommands';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { isTextType, isShapeWithText } from '../../lib/selection';
import { pxToPt, ptToPx, isExactLineHeight, exactLineHeightPx, exactLineHeight, lineHeightMultiple } from '../../lib/units';
import { Ruler, X, MoveHorizontal, MoveVertical, RotateCw, ArrowLeftRight, ArrowUpDown, TextCursorInput, Scaling, Space, AlignJustify } from 'lucide-react';

/** Compact spin field used by the Measurement pane (commit on blur / Enter, arrow keys step). */
const fmtMeasure = (v, digits) => (v === null || v === undefined || Number.isNaN(Number(v)) ? '' : String(Math.round(Number(v) * 10 ** digits) / 10 ** digits));
const MeasureField = ({ icon: Icon, label, value, onChange, min = -99999, max = 99999, step = 1, suffix, digits = 2, disabled, testId }) => {
  const fmt = (v) => fmtMeasure(v, digits);
  const [local, setLocal] = useState(fmt(value));
  useEffect(() => setLocal(fmtMeasure(value, digits)), [value, digits]);
  const commit = () => {
    const n = parseFloat(local);
    if (!Number.isNaN(n) && n !== Number(value)) onChange(Math.max(min, Math.min(max, n)));
    else setLocal(fmt(value));
  };
  const stepBy = (d) => { const n = (parseFloat(local) || 0) + d; onChange(Math.max(min, Math.min(max, Math.round(n * 1000) / 1000))); };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <label className={`flex items-center gap-1.5 h-8 rounded-md border border-input bg-background px-1.5 min-w-0 focus-within:ring-1 focus-within:ring-ring ${disabled ? 'opacity-50' : ''}`}>
          <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
          <span className="sr-only">{label}</span>
          <input
            type="text"
            inputMode="decimal"
            value={local}
            disabled={disabled}
            aria-label={label}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); stepBy(e.shiftKey ? step * 10 : step); }
              else if (e.key === 'ArrowDown') { e.preventDefault(); stepBy(-(e.shiftKey ? step * 10 : step)); }
              e.stopPropagation();
            }}
            className="h-full w-full min-w-0 bg-transparent text-[12px] font-mono text-foreground focus:outline-none disabled:cursor-not-allowed"
            data-testid={testId}
          />
          {suffix && <span className="text-[10px] text-muted-foreground shrink-0">{suffix}</span>}
        </label>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">{label}</TooltipContent>
    </Tooltip>
  );
};

/**
 * Publisher-style floating Measurement pane: position, size and rotation of the selected object (in the canvas unit)
 * and, for text, tracking / text scaling / kerning / line spacing. Toggled from Size ▸ Measurement or the status bar
 * (`viewOptions.measurement`).
 */
export const MeasurementPane = () => {
  const { state, dispatch, rawDispatch, getSelectedObjects, pxToUnit, unitToPx } = useEditor();
  const t = useTextCommands();
  const selected = getSelectedObjects();
  const one = selected.length === 1 ? selected[0] : null;
  const many = selected.length > 1;
  const textTarget = t.textObjs[0] && (isTextType(t.textObjs[0]) || isShapeWithText(t.textObjs[0], state.editingObjectId)) ? t.textObjs[0] : null;
  const u = state.canvasUnit;
  const digits = u === 'px' ? 0 : u === 'pt' ? 1 : 2;
  const stepU = u === 'px' ? 1 : u === 'mm' ? 1 : u === 'cm' ? 0.1 : u === 'pt' ? 1 : 0.05;
  // line spacing: a multiple ("sp") or an exact height in points (Paragraph dialog ▸ Exactly) — edited in its own terms
  const lineExact = isExactLineHeight(t.currentLineHeight);

  // The pane can be dragged around by its header (position is remembered for the session).
  const [pos, setPos] = useState(null); // { x, y } relative to the workspace, null = default corner
  const paneRef = useRef(null);
  const dragRef = useRef(null);
  const onHeaderPointerDown = (e) => {
    if (e.button !== 0 || e.target.closest('button')) return;
    const parent = paneRef.current?.offsetParent;
    if (!parent) return;
    const r = paneRef.current.getBoundingClientRect();
    const pr = parent.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, pw: pr.width, ph: pr.height, w: r.width, h: r.height, px: pr.left, py: pr.top };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };
  const onHeaderPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const x = Math.max(0, Math.min(d.pw - d.w, e.clientX - d.px - d.dx));
    const y = Math.max(0, Math.min(d.ph - d.h, e.clientY - d.py - d.dy));
    setPos({ x, y });
  };
  const onHeaderPointerUp = () => { dragRef.current = null; };

  const close = useCallback(() => rawDispatch({ type: 'SET_VIEW_OPTION', payload: { key: 'measurement', value: false } }), [rawDispatch]);
  if (!state.viewOptions?.measurement) return null;

  const upd = (data) => one && dispatch({ type: 'UPDATE_OBJECT', payload: { id: one.id, data } });
  const setText = (key, value) => t.setProp(key, value);
  const tp = t.props;
  const noSel = !one && !many;

  return (
    <TooltipProvider delayDuration={250}>
      <aside
        ref={paneRef}
        className="absolute z-[60] w-[292px] max-w-[calc(100vw-24px)] rounded-lg border border-border bg-card/95 text-card-foreground shadow-xl backdrop-blur-md"
        style={pos ? { left: pos.x, top: pos.y } : { left: 12, bottom: 12 }}
        data-testid="measurement-pane"
        data-keep-editing
        data-ribbon
        role="dialog"
        aria-label="Measurement"
      >
        <header
          className="flex items-center gap-2 px-3 h-9 border-b border-border cursor-move select-none touch-none"
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          onPointerCancel={onHeaderPointerUp}
          data-testid="measurement-pane-header"
        >
          <Ruler className="h-4 w-4 text-primary" />
          <h2 className="font-outfit text-sm font-semibold flex-1">Measurement</h2>
          <span className="text-[11px] text-muted-foreground" data-testid="measurement-pane-unit">{u}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={close} aria-label="Close Measurement" data-testid="measurement-pane-close"><X className="h-4 w-4" /></Button>
        </header>
        <div className="p-2.5 space-y-2">
          {noSel && <p className="text-xs text-muted-foreground px-0.5" data-testid="measurement-pane-empty">Select an object to see and edit its position, size and rotation.</p>}
          {many && <p className="text-xs text-muted-foreground px-0.5" data-testid="measurement-pane-multi">{selected.length} objects selected — select a single object to edit its measurements.</p>}
          <div className="grid grid-cols-2 gap-1.5" data-testid="measurement-pane-geometry">
            <MeasureField icon={MoveHorizontal} label={`Horizontal position (${u})`} value={one ? Number(pxToUnit(one.x)) : null} onChange={(v) => upd({ x: unitToPx(v) })} step={stepU} digits={digits} suffix="x" disabled={!one} testId="measurement-x" />
            <MeasureField icon={MoveVertical} label={`Vertical position (${u})`} value={one ? Number(pxToUnit(one.y)) : null} onChange={(v) => upd({ y: unitToPx(v) })} step={stepU} digits={digits} suffix="y" disabled={!one} testId="measurement-y" />
            <MeasureField icon={ArrowLeftRight} label={`Width (${u})`} value={one ? Number(pxToUnit(one.width)) : null} onChange={(v) => upd({ width: Math.max(1, unitToPx(v)) })} min={0} step={stepU} digits={digits} suffix="w" disabled={!one} testId="measurement-width" />
            <MeasureField icon={ArrowUpDown} label={`Height (${u})`} value={one ? Number(pxToUnit(one.height)) : null} onChange={(v) => upd({ height: Math.max(1, unitToPx(v)) })} min={0} step={stepU} digits={digits} suffix="h" disabled={!one} testId="measurement-height" />
            <MeasureField icon={RotateCw} label="Rotation (degrees)" value={one ? one.rotation || 0 : null} onChange={(v) => upd({ rotation: ((v % 360) + 360) % 360 })} min={-360} max={360} step={1} digits={1} suffix="°" disabled={!one} testId="measurement-rotation" />
          </div>
          {textTarget && (
            <div className="grid grid-cols-2 gap-1.5 border-t border-border pt-2" data-testid="measurement-pane-text">
              <MeasureField icon={TextCursorInput} label="Tracking (%)" value={tp.tracking ?? 100} onChange={(v) => setText('tracking', v)} min={25} max={400} step={1} digits={0} suffix="%" testId="measurement-tracking" />
              <MeasureField icon={Scaling} label="Text scaling (%)" value={tp.textScale ?? 100} onChange={(v) => setText('textScale', v)} min={10} max={600} step={1} digits={0} suffix="%" testId="measurement-text-scaling" />
              <MeasureField icon={Space} label="Kerning (pt between characters)" value={pxToPt(tp.letterSpacing || 0)} onChange={(v) => setText('letterSpacing', ptToPx(v))} min={-50} max={200} step={0.25} digits={2} suffix="pt" testId="measurement-kerning" />
              {lineExact
                ? <MeasureField icon={AlignJustify} label="Line spacing (exactly, pt)" value={pxToPt(exactLineHeightPx(t.currentLineHeight))} onChange={(v) => t.setLineSpacing(exactLineHeight(v))} min={1} max={400} step={1} digits={2} suffix="pt" testId="measurement-line-spacing" />
                : <MeasureField icon={AlignJustify} label="Line spacing (lines)" value={lineHeightMultiple(t.currentLineHeight)} onChange={(v) => t.setLineSpacing(v)} min={0.5} max={6} step={0.1} digits={2} suffix="sp" testId="measurement-line-spacing" />}
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
};
